import React, { useState, useEffect, useRef } from 'react';
import { isMobile } from '@/biz/tool';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import PriceFieldsComponent from '../PriceFieldsComponent';
import { useMultiplePricesStore } from '@/hooks/useMultiplePricesStore';
import axiosInstance from '@/apis/axiosInstance';
import { useTranslation } from 'react-i18next';
import {
  resolveAhaPicksOfferItem,
  resolveAcceptOfferDisplayPrice
} from '../AhaPicksPrePriceFields';
import AudienceDemographics from './audienceDemographics';
import { AudienceViewSource, SelfEstimatedStatus } from './config';

const AnalysisContent: React.FC<{
  selectCountryError?: boolean;
  setSelectCountryError?: (v: boolean) => void;
  audienceError?: boolean;
  setAudienceError?: (v: boolean) => void;
  onPriceValidChange: (isValid: boolean) => void;
}> = ({
  selectCountryError = false,
  setSelectCountryError = () => {},
  audienceError = false,
  setAudienceError = () => {},
  onPriceValidChange
}) => {
  const { t } = useTranslation();
  const { detailData } = useDetailDataStore();
  const {
    showModalConfirmDealPrice,
    showModalAcceptAhaPicksOffer,
    thisLoading
  } = useWebModalStore();
  const {
    setRateNonNegotiable,
    selectedPriceFields,
    setCompetitivePriceDetail,
    newOrderPrice,
    setNewOrderPrice
  } = useMultiplePricesStore();

  const [nonNegotiable, setNonNegotiable] = useState(false);
  const [modalSession, setModalSession] = useState(0);
  const [hasAudienceData, setHasAudienceData] = useState(false);

  useEffect(() => {
    if (!showModalConfirmDealPrice) {
      return;
    }

    setModalSession((session) => session + 1);

    if (showModalAcceptAhaPicksOffer) {
      const selected = useMultiplePricesStore.getState().selectedPriceFields[0];
      const offerItem = selected ?? resolveAhaPicksOfferItem(detailData?.order);
      const acceptPrice = resolveAcceptOfferDisplayPrice(offerItem);
      if (acceptPrice != null) {
        setNewOrderPrice(acceptPrice);
      }
      return;
    }

    if (detailData?.order?.fixedCreatorEarnAmount) {
      setNewOrderPrice(detailData.order.fixedCreatorEarnAmount);
      setNonNegotiable(true);
    }
  }, [
    showModalConfirmDealPrice,
    showModalAcceptAhaPicksOffer,
    detailData?.order
  ]);

  const competitivePriceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  useEffect(() => {
    if (competitivePriceTimerRef.current) {
      clearTimeout(competitivePriceTimerRef.current);
    }
    const price = Number(newOrderPrice);
    if (
      !price ||
      price <= 0 ||
      selectedPriceFields.length === 0 ||
      !detailData?.order?.id
    ) {
      return;
    }
    competitivePriceTimerRef.current = setTimeout(async () => {
      try {
        const params = {
          interestedPriceField: {
            ...selectedPriceFields[0],
            actualGet: price
          }
        };
        const { data } = await axiosInstance.post(
          `/order/${detailData.order.id}/competitivePrice`,
          params
        );
        const detail = data?.data?.interestedPriceField;
        if (detail) {
          setCompetitivePriceDetail(detail);
        }
      } catch (error) {
        console.error('competitivePrice error:', error);
      }
    }, 500);
    return () => {
      if (competitivePriceTimerRef.current) {
        clearTimeout(competitivePriceTimerRef.current);
      }
    };
  }, [newOrderPrice, selectedPriceFields, detailData?.order?.id]);

  useEffect(() => {
    // 有一方数据，或者自研数据已经成功生成，则算作有数据，不进行展示受众模块
    if (
      detailData?.audienceView?.source === AudienceViewSource.FIRST_PARTY ||
      (detailData?.audienceView?.source === AudienceViewSource.SELF_ESTIMATED &&
        detailData?.audienceView?.status === SelfEstimatedStatus.SUCCESS)
    ) {
      setHasAudienceData(true);
    }
  }, [detailData?.audienceView]);

  useEffect(() => {
    setRateNonNegotiable(nonNegotiable);
  }, [nonNegotiable]);

  return (
    <div
      className={`h-full self-stretch pt-4 flex flex-col items-start gap-3 flex-1 min-w-0 min-h-0 ${isMobile() ? '' : 'border-r border-[#E5E5E5] pl-1'}`}
    >
      <div className="w-full flex flex-col gap-3 flex-1 min-h-0 overflow-y-auto pr-5 pb-6">
        <div className="text-[#0D0D0D] text-base font-semibold font-['Plus Jakarta Sans'] leading-snug">
          {t('modal.confirm_deal_price.select_content_type', {
            defaultValue: 'Select content type'
          })}{' '}
        </div>
        <PriceFieldsComponent
          key={modalSession}
          direction={isMobile() ? 'flex flex-col' : 'flex flex-row'}
          hasRadioButton={true}
          orderPriceAdjustment={true}
          newOrderPrice={newOrderPrice}
          setNewOrderPrice={setNewOrderPrice}
          nonNegotiable={nonNegotiable}
          setNonNegotiable={setNonNegotiable as (v: boolean) => void}
          isModal={true}
          onPriceRangeValidChange={onPriceValidChange}
          selectCountryError={selectCountryError}
          setSelectCountryError={setSelectCountryError}
        />
        {/* 受众模块展示  非移动端 & （是本次接单上传受众截图 || 判断有没有数据） */}
        {!isMobile() && (thisLoading || !hasAudienceData) && (
          <AudienceDemographics
            audienceError={audienceError}
            setAudienceError={setAudienceError}
          />
        )}
      </div>
    </div>
  );
};

export default AnalysisContent;
